import d3 from "d3";
import $ from "jquery";
import R from "ramda";
import createPopup from "../page/createPopup.js";

const KERNEL_Y_SPACING = 10;
const KERNEL_X_PADDING = 2;
const KERNAL_HEIGHT = 5;
const LABEL_FONT_SIZE = "11px";
const HEADER_FONT_SIZE = "11px";
const BUTTON_HEIGHT = 32;

const DECILE_HEIGHT = 4;
const DECILE_SPACING = 6;
const DECILE_GROUP_PADDING = 10; // vertical padding between the deciles and the viz

const PAGINATE_DURATION = 400;

export default function createCorncob(initialState) {

    // defaults, and this object will hold the previous state after setState
    let prevState = {
        selector: "",
        width: 0,
        indicators: {},
        locations: {},
        rankings: {},
        currentDecile: 1, // number 1 - 10
        hoveredKernel: null, // { locationId, indicatorId }
        hoveredDecile: null, // number 1-10
        sortBy: "OVERALL",
        sortDir: "asc" // "asc" or "desc"
    };

    // merge the initial state in with the default state
    let state = Object.assign({}, prevState, initialState);

    // merge the new state and re-render
    function setState(newState) {

        // copy the old state into prevState
        prevState = Object.assign({}, state);

        Object.assign(state, newState);
        render();
    }

    // helper to determine if a state key has changed
    function hasChanged(key){
        return prevState[key] !== state[key];
    }


    // helper functions
    const getProp = R.curry((prop, indicatorId, locationId) => state.rankings[indicatorId][locationId].years[1][prop]);
    const getRank     = getProp("rank");
    const getQuintile = getProp("quintile");
    const getDecile   = getProp("decile");
    const getIndicatorRank = (indicatorId, locationId) => getRank(indicatorId, locationId);
    const valueComparator = (indicator, direction = "asc") => {
        return function(a, b) {
            const aRank = getIndicatorRank(indicator, a.id);
            const bRank = getIndicatorRank(indicator, b.id);
            return (state.sortDir === "asc") ? R.subtract(aRank, bRank) : R.subtract(bRank, aRank);
        };
    };

    // flag
    let isAnimating = false;


    const getLocationName = (locationId) => state.locations[locationId].name;
    const getIndicatorName = (indicatorId) => state.indicators[indicatorId].shortName;



    // each of the columns, Overall Performance, etc
    const columns = ["OVERALL", "a100", "q100", "u100", "h100"];

    const margin = { top: 0, right: 0, bottom: 0, left: 0};

    // add some padding on the bottom for small screens that will scroll
    // this container, and for the "next" button
    d3.select(state.selector).style({ "padding-bottom": "26px" });

    const title = d3.select(state.selector).append("div")
        .attr({ "class": "corncob__title viz-label" })
        .text("Performance by Dimension");

    const header = d3.select(state.selector).append("div")
        .attr("class", "corncob__header");

    // this is mostly here to simplify the math of where to place the popup.
    // it will be relative to this
    const vizHolder = d3.select(state.selector).append("div")
        .attr("class", "corncob__viz-holder")
        .style("position", "relative");

    const svg = vizHolder.append("svg")
        .style({ "overflow": "visible" });


    const getNext = (current) => current  % 10 + 1;
    const getPrev = (current) => (current - 2 + 10) % 10 + 1;

    const prevDecilesGroup = svg.append("g")
        .attr("class", "prev-deciles");

    // prev label/button
    const prevButtonG = prevDecilesGroup.append("g")
        .attr({
            "class": "prev-deciles__label",
            "text-anchor": "end"
        })
        .on("click", (e) => {
            setState({
                // if we're sorting desc, everything is backwards
                currentDecile: (state.sortDir === "asc")
                    ? getPrev(state.currentDecile)
                    : getNext(state.currentDecile)
            });
        });

    const prevButton = createButton(prevButtonG, "up");
    prevButton.setText("Prev");

    function deselectKernel(d){
        if (isAnimating) { return; }
        setState({ hoveredKernel: null });

        window.removeEventListener("touchstart", deselectKernel, true);
    }

    const vizGroup = svg.append("g")
        .attr("class", "corncob")
        .on("mouseleave", deselectKernel);

    const nextDecilesGroup = svg.append("g")
        .attr("class", "next-deciles");

    // next label/button
    const nextButtonG = nextDecilesGroup.append("g")
        .attr({ "class": "next-deciles__label"})
        .on("click", (e) => {
            setState({
                hoveredDecile: null,
                currentDecile: (state.sortDir === "asc")
                    ? getNext(state.currentDecile)
                    : getPrev(state.currentDecile)
            });
        });

    const nextButton = createButton(nextButtonG, "down");
    nextButton.setText("Next");

    const popup = createPopup({
        container: vizHolder.node(),
        width: 320,
        triangle: "top",
        isHidden: true,
        closeBtn: false,
        flipToContain: true
    });


    // build column headers
    header.selectAll("div.corncob__header-column")
        .data(columns)
        .enter()
        .append("div")
        .attr("class", "corncob__header-column")
        .text(id => getIndicatorName(id))
        .on("click", (d) => {

            const sortDir = (d === state.sortBy && state.sortDir === "asc") ? "desc" : "asc";
            // reset page to 1 or 10
            const currentDecile = (sortDir === "asc") ? 1 : 10;

            // change the sort
            setState({
                sortBy: d,
                sortDir,
                currentDecile
            });
        });


    // render on init
    render();

    function render() {

        // use a flag to disabled kernel hovers while the page is animating
        isAnimating = hasChanged("currentDecile")
                   || hasChanged("sortDir")
                   || hasChanged("sortBy");

        // reset the flag after the animation is finished
        if (isAnimating){
            setTimeout(() => {
                isAnimating = false;
            }, PAGINATE_DURATION);
        }

        // prepare data
        // order locations by overall rank ("OVERALL" is the key)
        const rankedLocations = R.values(state.locations).sort(valueComparator(state.sortBy, state.sortDir));

        // show only the locations in this selceted decile (page)
        const filteredLocations = rankedLocations.filter((l, i) => {
            return getDecile(state.sortBy, l.id) === state.currentDecile;
        });


        /* calculates dimensions */
        const prevDecilesNumbers = (state.sortDir === "asc")
            ? R.range(1, state.currentDecile)
            : R.reverse(R.range(state.currentDecile + 1, 11));
        const nextDecilesNumbers = (state.sortDir === "asc")
            ? R.range(state.currentDecile + 1, 11)
            : R.reverse(R.range(1, state.currentDecile));
        // the height needs to fit all the location rows
        const vizGroupHeight = filteredLocations.length * (KERNEL_Y_SPACING + KERNAL_HEIGHT);
        const prevDecilesHeight = prevDecilesNumbers.length * (DECILE_HEIGHT + DECILE_SPACING) + DECILE_GROUP_PADDING;
        const nextDecilesHeight = nextDecilesNumbers.length * (DECILE_HEIGHT + DECILE_SPACING) + DECILE_GROUP_PADDING;
        const vizWidth  = state.width - margin.left - margin.right;
        const vizHeight = vizGroupHeight + prevDecilesHeight + nextDecilesHeight - margin.top  - margin.bottom;
        const LABEL_WIDTH = 190;

        svg.attr("width", vizWidth + margin.left + margin.right)
           .attr("height", vizHeight + margin.top + margin.bottom);

        vizGroup.transition().duration(PAGINATE_DURATION)
        .attr({
            "transform": `translate(${margin.left}, ${margin.top + prevDecilesHeight})`,
            "width": vizWidth
        });

        // scales for locations and kernels
        const xScale = d3.scale.ordinal()
            .rangeBands([LABEL_WIDTH, vizWidth])
            .domain(columns);

        const yScale = d3.scale.ordinal()
            .rangeBands([0, vizGroupHeight])
            .domain(filteredLocations.map(d => d.id));


        // const colors = ["#efd4e4", "#cc95b9", "#a9438c", "#683057", "#2c0f24"];
        // const colors = ["#bde3f3", "#80accd", "#0e6bab", "#1c4a66", "#091e27"];
        // const colors = ["#c9e8f2", "#8cbacc", "#3bbfe2", "#166e91", "#0b1b28"];
        const colors = ["#b1f8ed", "#59bece", "#3484bc", "#22478c", "#060636"];


        const colorScale = d3.scale.ordinal()
            .range(colors)
            .domain([1,2,3,4,5]);


        title.style({
            "padding-left": `${LABEL_WIDTH}px`
        });

        // column headers
        header.selectAll(".corncob__header-column")
            .classed("is-asc", d => (d === state.sortBy && state.sortDir === "asc"))
            .classed("is-desc", d => (d === state.sortBy && state.sortDir === "desc"))
            .style({
                "flex-grow": 0,
                "flex-shrink": 0,
                "flex-basis": d => `${xScale.rangeBand()}px`,
                "width": d => `${xScale.rangeBand()}px`,
                "font-size": HEADER_FONT_SIZE,
                // "font-weight": "bold",
                "line-height": HEADER_FONT_SIZE,
                "display": "flex",
                "align-items": "center",
                "box-sizing": "border-box"
            });



        // common function add lines to prev/next deciles
        function addDecileLines(decile) {

            const decileLineAttrs = {
                "class": "decile__line",
                "x": LABEL_WIDTH + KERNEL_X_PADDING/2,
                "y": d => (state.hoveredDecile === d) ? 0 : DECILE_SPACING/2,
                "width": vizWidth - LABEL_WIDTH - KERNEL_X_PADDING/2,
                "height": d => (state.hoveredDecile === d) ? DECILE_HEIGHT + DECILE_SPACING : DECILE_HEIGHT,
                "rx": d => (state.hoveredDecile === d) ? (DECILE_HEIGHT + DECILE_SPACING)/2 : DECILE_HEIGHT/2,
                "ry": d => (state.hoveredDecile === d) ? (DECILE_HEIGHT + DECILE_SPACING)/2 : DECILE_HEIGHT/2,
                "fill": (d, i) => colors[Math.ceil(d/2) - 1]
            };

            // enter
            const gEnter = decile.enter().append("g")
                .attr({ "class": "decile-g" })
                .style({ "cursor": "pointer" });

            gEnter.append("rect")
                .attr({"class": "decile__line"});

            gEnter.append("rect")
                .attr({
                    "class": "decile__hit-area",
                    "x": decileLineAttrs.x,
                    "y": d => 0,
                    "width": decileLineAttrs.width,
                    "height": DECILE_HEIGHT + DECILE_SPACING,
                    "fill": "transparent"
                })
                .on("mouseenter", function(d){
                    if (isAnimating) { return; }
                    setState({ "hoveredDecile": d });
                })
                .on("mouseleave", function(d){
                    if (isAnimating) { return; }
                    setState({ "hoveredDecile": null });
                })
                .on("click", (d) => setState({
                    hoveredDecile: null,
                    currentDecile: d
                }));

            const decileLabelG = gEnter.append("g")
                .attr({
                    "class": "decile__label",
                    "opacity": 0,
                    "pointer-events": "none",
                    "transform": `translate(${decileLineAttrs.x - 3}, -${BUTTON_HEIGHT/2 - (DECILE_HEIGHT + DECILE_SPACING)/2})`
                });

            createButton(decileLabelG);


            // exit
            decile.exit().remove();

            // update
            decile.attr({
                "transform": (d, i) => {
                    const top = i * (DECILE_HEIGHT + DECILE_SPACING);
                    const halfway = top + (DECILE_HEIGHT + DECILE_SPACING)/2;
                    const middle = halfway - DECILE_HEIGHT/2;
                    return `translate(0, ${middle})`;
                }
            }),
            decile.select(".decile__line")
                .transition()
                .duration(100)
                .attr(decileLineAttrs);

            decile.select(".decile__label")
                .attr({
                    "opacity": d => state.hoveredDecile === d ? 1 : 0
                });

        }

        prevDecilesGroup.select(".prev-deciles__label")
            .attr("transform", `translate(${LABEL_WIDTH - 3}, ${(prevDecilesHeight || 0) - BUTTON_HEIGHT - DECILE_SPACING})`)
            .style({
                // hide it if it's not needed
                "opacity": d => (prevDecilesNumbers.length > 0 && !state.hoveredDecile) ? 1 : 0,
                "cursor": "pointer"
            });

        // prevButton.setDecile((state.sortDir === "asc") ? state.currentDecile - 1 : state.currentDecile + 1);

        const prevDeciles = prevDecilesGroup.selectAll(".decile-g")
            .data(prevDecilesNumbers, d => d);
        addDecileLines(prevDeciles);


        nextDecilesGroup.select(".next-deciles__label")
            .attr("transform", `translate(${LABEL_WIDTH - 3}, 0)`)
            .style({
                // hide it if it's not needed
                "opacity": d =>(nextDecilesNumbers.length > 0 && !state.hoveredDecile) ? 1 : 0,
                "cursor": "pointer"
            });

        // nextButton.setDecile((state.sortDir === "asc") ? state.currentDecile + 1 : state.currentDecile - 1);


        const nextDeciles = nextDecilesGroup.selectAll(".decile-g")
            .data(nextDecilesNumbers, d => d);

        // make the nextDeciles group always be below the vizGroup
        nextDecilesGroup.transition().duration(PAGINATE_DURATION)
            .attr("transform", `translate(0, ${vizGroupHeight + prevDecilesHeight + DECILE_GROUP_PADDING})`);
        addDecileLines(nextDeciles);


        // each line in the corncob
        const locationGroups = vizGroup.selectAll("g.location")
            .data(filteredLocations, d => d.id);



        // if the user is navigating forward or backward
        const navigationDir = (state.currentDecile > prevState.currentDecile) ? "asc" : "desc";

        /* locationGroups */
        // locationGroups enters
        const locationGroupsEnters = locationGroups.enter()
            .append("g").attr("class", "location")
            .attr("transform", d => {
                // determine which direction the locations should flow
                return (navigationDir === state.sortDir) // asc or desc
                    ? `translate(0, ${vizGroupHeight})` // bottom to top
                    : "translate(0, 0)";                // top to bottom
            });

        // locationGroups exits
        const locationGroupsExits = locationGroups.exit();

        locationGroupsExits.transition().duration(PAGINATE_DURATION)
            .attr("transform", d => {
                // determine which direction the locations should flow
                return (navigationDir === state.sortDir)
                    ? "translate(0, 0)"                  // bottom to top
                    : `translate(0, ${vizGroupHeight})`; // top to bottom
            })
            .remove();

        // locationGroups updates
        locationGroups
            .transition().duration(PAGINATE_DURATION)
            .attr("transform", d => `translate(0, ${yScale(d.id)})`);


        /* kernel__pill */
        // kernel__pill enter
        locationGroupsEnters.selectAll(".kernel__pill")
            .transition().duration(PAGINATE_DURATION)
            .attr("height", 0);

        // kernel__pill exit
        locationGroupsExits.selectAll(".kernel__pill")
            .transition().duration(PAGINATE_DURATION)
            .attr("height", 0);



        /* corncob__label */
        // corncob__label enter
        const labelEnter = locationGroupsEnters.append("text")
            .attr({
                "class": "corncob__label",
                "text-anchor": "end",
                // I got these crazy numbers from when these labels used to be in a d3.svg.axis()
                // 7.5 was a px offset on the <g> container, .32 was em on the <text> (the <g> is 15px tall)
                "dy": 7.5 + (parseInt(LABEL_FONT_SIZE) * .32),
                "x": LABEL_WIDTH - 3
            })
            .style({
                "font-size": LABEL_FONT_SIZE,
                "opacity": 0
            });
        labelEnter.append("tspan").attr("class", "location");
        labelEnter.append("tspan").attr("class", "rank")
            .attr("dx", 6); // space between location and rank


        // corncob__label exit
        locationGroupsExits.selectAll(".corncob__label")
            .transition().duration(PAGINATE_DURATION)
            .style({"opacity": 0});

        // corncob__label update
        locationGroups.selectAll(".corncob__label")
            .transition().duration(PAGINATE_DURATION)
            .style({"opacity": 1});

        locationGroups.select(".location").text(d => ` ${getLocationName(d.id)} `);
        // the space at the begining is important
        locationGroups.select(".rank").text(d => ` ${getIndicatorRank(state.sortBy, d.id)}`);


        /* kernels */
        // each column in each line
        const kernelsGroup = locationGroups.selectAll("g.kernel")
          .data(d => columns.map(indicatorId => ({
              locationId: d.id,
              indicatorId
          })), d => d.locationId + d.indicatorId);

        // not using rangeBands padding so we can specify a pixel value (rather than percentage)
        const kernelHeight = Math.min(yScale.rangeBand() - KERNEL_Y_SPACING, KERNAL_HEIGHT);
        const kernelWidth  = xScale.rangeBand() - KERNEL_X_PADDING*2;
        const getKernelY = (d) => yScale.rangeBand()/2 - kernelHeight/2;
        const getKernelX = (d) => xScale(d.indicatorId) + KERNEL_X_PADDING;
        const kernelIsHovered = ({indicatorId, locationId}) => {
            if (!state.hoveredKernel) { return false; }
            return indicatorId === state.hoveredKernel.indicatorId
                && locationId  === state.hoveredKernel.locationId;
        };

        // kernel group enter
        const kernelGroupEnter = kernelsGroup.enter().append("g")
            .attr("class", "kernel");

        kernelGroupEnter.append("rect")
            .attr({ "class": "kernel__pill" })
            .style({ "fill": d => colorScale(getQuintile(d.indicatorId, d.locationId)) });

        function kernelEnter(d){
            if (isAnimating) { return; }
            setState({ "hoveredKernel": d });
        }


        kernelGroupEnter.append("rect")
            .attr("class", "kernel__hit-area")
            .on("mouseenter", kernelEnter)
            .on("touchstart", d => {
                window.addEventListener("touchstart", deselectKernel, true);
                kernelEnter(d);
            });

        // kernel group update
        kernelsGroup.select(".kernel__pill")
            .transition()
            .duration(250)
            .attr({
                "x": getKernelX,
                "y": d => (kernelIsHovered(d)) ? 0 : getKernelY(d), // vertially center
                "width": kernelWidth,
                "height":  d => (kernelIsHovered(d)) ? yScale.rangeBand() : kernelHeight,
                "rx": d => (kernelIsHovered(d)) ? yScale.rangeBand()/2 : kernelHeight/2,
                "ry": d => (kernelIsHovered(d)) ? yScale.rangeBand()/2 : kernelHeight/2
            });

        kernelsGroup.select(".kernel__hit-area")
            .attr({
                "x": d => xScale(d.indicatorId),
                "y": d => 0,
                "width": xScale.rangeBand(),
                "height": yScale.rangeBand()
            })
            .style("fill", d => "transparent");



        // popup
        if (state.hoveredKernel){
            const d = state.hoveredKernel;

            const quintileNumber = getQuintile(d.indicatorId, d.locationId);
            const suffix = (quintileNumber === 1) ? "st"
                         : (quintileNumber === 2) ? "nd"
                         : (quintileNumber === 3) ? "rd" : "th";
            const rankNumber = getRank(d.indicatorId, d.locationId);
            const totalLocations = Object.keys(state.rankings["OVERALL"]).length;


            const html = $(`
                <div class="corncob__popup">
                    <div class="kernel__rank">
                        <span class="number"><sup></sup>${rankNumber}</span>
                        <span class="out-of">out of ${totalLocations} in ${getIndicatorName(d.indicatorId)}</span>
                    </div>
                    <div class="kernel__quintile">
                        <span class="swatch" style="background-color: ${colors[quintileNumber-1]}"></span>
                        <span class="number">${quintileNumber}<sup>${suffix}</sup></span> Quintile
                    </div>
                </div>`);

            popup.setState({
                title: `<span class="location-label">${getLocationName(d.locationId)}</span>`,
                html: html,
                top:  margin.top + prevDecilesHeight + yScale(d.locationId) + yScale.rangeBand()/2,
                offset: -1 * Math.ceil(yScale.rangeBand()/2),
                left: getKernelX(d) + kernelWidth/2,
                isHidden: false
            });
        }
        else {
            popup.hidePopup();
        }

    }

    // external api
    return {
        setState,
        setWidth: (width) => setState({ width })
    };

}





function createButton(g, arrowDirection = "none") {

    const HEIGHT = BUTTON_HEIGHT;
    const FONT_SIZE = 14;
    const FILL = (arrowDirection === "none") ? "transparent" : "#ebf4fb";
    const FILL_HOVER = "#dee9f1";
    // const WIDTH = (arrowDirection === "none") ? 80 : 100;
    const WIDTH = (arrowDirection === "none") ? 80 : 54;


    const background = g.append("rect")
        .attr({
            "class": "button__background",
            "y": 0,
            "x": d => (d < 10) ? `-${WIDTH}px` : `-${WIDTH + 8}px`,
            "width": d => (d < 10) ? `${WIDTH}px` : `${WIDTH + 8}px`,
            "height": `${HEIGHT}px`,
            "fill": FILL,
            "rx": 3,
            "ry": 3
        });

    g.on("mouseenter", () => background.attr("fill", FILL_HOVER));
    g.on("mouseleave", () => background.attr("fill", FILL));

    const textEl = g.append("text")
        .attr({
            "class": "button__text",
            "dx": "-5px",
            "dy": `${HEIGHT/2 + FONT_SIZE * .32}px`
        })
        .style({
            "font-size": FONT_SIZE,
            "fill": "#3b69a5",
            "text-anchor": "end"
        })
        // default the text to this decile
        .text(d => `View ${getDecileText(d)}`);

    // arrow
    const arrow = (arrowDirection !== "none") ? (
        g.append("path")
        .attr({
            "d": getArrowD(WIDTH),
            "stroke": "#3b69a5",
            "stroke-width": 2,
            "stroke-linecap": "round",
            "x": 0
        })
    ) : null;

    function getArrowD(width) {
        return (arrowDirection === "up")
            ? `M${6-width},10 l0,10 M${6-width},10 l-5,5 M${6-width},10 l5,5`
            : `M${6-width},10 l0,10 M${6-width},20 l-5,-5 M${6-width},20 l5,-5`;
    }

    function getDecileText(decile){
        const ordinal = (decile === 1) ? "st"
                      : (decile === 2) ? "nd"
                      : (decile === 3) ? "rd"
                      : "th";

        return (decile <= 0 || decile > 10)
            ? ""
            : `${decile}${ordinal} Decile`;
    }

    return {
        setDecile: function(decile){

            // grow the button if it's 10
            const width = (decile < 10) ? WIDTH : WIDTH + 8;
            background.attr({
                "x": `-${width}px`,
                "width": width
            });

            if (arrow){
                arrow.attr({ "d": getArrowD(width) });
            }

            textEl.text(getDecileText(decile));
        },

        setText: function(text){
            textEl.text(text);
        }
    };

}
